/**
 * The one shared chat component (spec 022): the scene assistant's tab and the
 * story assistant (spec 024) both wrap it. It knows nothing about scenes or
 * stories; the caller owns the conversation and the turn.
 *
 * - Bubbles: user right, assistant left; notices and errors are one-liners.
 * - The newest assistant message reveals its bites at the D-7 pace with a
 *   typing indicator; a click in the thread, a key in the panel or sending
 *   reveals the rest. Reduced motion shows everything at once.
 * - Autoscroll while at the bottom; a "New messages ↓" control otherwise.
 * - Enter sends, Shift+Enter is a newline, Up/Down recall prompts (D-6),
 *   Escape stops a running turn or ends recall. The input stays editable
 *   while a turn runs; Stop replaces Send.
 * - The draft is kept per `conversationKey` for the editor session.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { biteDelay, bitesOf, type ChatBite, type ChatList } from './bites';
import { useChatDrafts } from './chatDrafts';
import {
  NOT_RECALLING,
  caretOnFirstLine,
  caretOnLastLine,
  recallDown,
  recallList,
  recallUp,
  type RecallState,
} from './promptRecall';
import styles from './ChatPanel.module.scss';

export interface ChatMessage {
  id: number | string;
  /** `notice` (Stopped, scene changed) and `error` render as one-liners. */
  role: 'user' | 'assistant' | 'notice' | 'error';
  text: string;
  /** Assistant only: list bites after the reply, in order (the Changes list; 024's missing media). */
  lists?: ChatList[];
  /** A notice or error line that offers Retry. */
  retry?: boolean;
  /** Assistant only (spec 031): one muted line under the reply, e.g. "Looked at the stage". */
  looked?: string;
}

export interface ChatPanelProps {
  /** Namespaced per surface (e.g. the scene id, `story:<id>`): drafts and recall are per key. */
  conversationKey: string;
  messages: readonly ChatMessage[];
  /** A turn is in flight: the thinking indicator and Stop show. */
  running: boolean;
  send: (text: string) => void;
  stop: () => void;
  /** Retry a notice or error line that has `retry`. */
  retry?: (messageId: ChatMessage['id']) => void;
  /** Shown as buttons in an empty conversation; a click fills the input. */
  examplePrompts: readonly string[];
  emptyHint?: string;
  /** The input cannot be used (e.g. the assistant is not available). */
  disabled?: boolean;
  /** A notice above the thread (e.g. why the input is disabled). */
  notice?: { text: string; testId?: string };
  placeholder?: string;
  inputLabel?: string;
  /** Per-message actions (the scene's Undo/Redo), rendered under the message. */
  renderActions?: (message: ChatMessage) => React.ReactNode;
  /** A status beside the thinking indicator while a turn runs (024: "Building your story… 12 s"). */
  thinkingText?: React.ReactNode;
  /** Appended to a notice or error line that offers Retry (025: "or open Under the hood"). */
  retryExtra?: React.ReactNode;
}

/** Within this many px of the bottom counts as at the bottom. */
export const AT_BOTTOM_PX = 24;

const EMPTY_DRAFT = '';

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const Dots: React.FC<{ label: string; animated: boolean; testId: string }> = ({ label, animated, testId }) => (
  <div className={`${styles.bubble} ${styles.assistant} ${styles.dotsBubble}`}>
    <span
      role="img"
      aria-label={label}
      className={`${styles.dots} ${animated ? styles.animated : ''}`}
      data-animated={animated ? 'true' : 'false'}
      data-testid={testId}
    >
      <span /><span /><span />
    </span>
  </div>
);

const BiteView: React.FC<{ bite: ChatBite }> = ({ bite }) => {
  if (bite.kind === 'text') {
    return <div className={`${styles.bubble} ${styles.assistant}`} data-testid="assistant-bite">{bite.text}</div>;
  }
  return (
    <div className={`${styles.bubble} ${styles.assistant} ${styles.list}`} data-testid={bite.testId ?? 'assistant-list'}>
      {bite.title && <div className={styles.listTitle}>{bite.title}</div>}
      {bite.content ?? <ul>{bite.items.map((item, i) => <li key={i}>{item}</li>)}</ul>}
    </div>
  );
};

/** A per-message action button (the scene's Undo/Redo), styled with the chat. */
export const ChatAction: React.FC<{ onClick: () => void; testId?: string; children: React.ReactNode }> = ({ onClick, testId, children }) => (
  <button type="button" className={styles.action} onClick={onClick} data-testid={testId}>{children}</button>
);

/** The whole panel when the assistant cannot be used at all: one notice, no input. */
export const ChatUnavailable: React.FC<{ text: string; testId?: string }> = ({ text, testId }) => (
  <div className={styles.panel} data-testid="assistant-panel">
    <p className={styles.notice} data-testid={testId}>{text}</p>
  </div>
);

export const ChatPanel: React.FC<ChatPanelProps> = ({
  conversationKey,
  messages,
  running,
  send,
  stop,
  retry,
  examplePrompts,
  emptyHint = 'Describe what should happen.',
  disabled = false,
  notice,
  placeholder,
  inputLabel = 'Message the assistant',
  renderActions,
  thinkingText,
  retryExtra,
}) => {
  const draft = useChatDrafts((s) => s.drafts[conversationKey] ?? EMPTY_DRAFT);
  const setDraftFor = useChatDrafts((s) => s.setDraft);
  const setDraft = useCallback((value: string) => setDraftFor(conversationKey, value), [setDraftFor, conversationKey]);

  const recall = useRef<RecallState>(NOT_RECALLING);
  const prompts = useMemo(() => recallList(messages), [messages]);
  const reducedMotion = useMemo(prefersReducedMotion, []);

  // Messages present when the panel mounted show whole; later assistant
  // messages reveal bite by bite.
  const [mountIds] = useState(() => new Set(messages.map((m) => m.id)));
  const [progress, setProgress] = useState<Record<string, number>>({});
  const bitesById = useMemo(() => {
    const map = new Map<ChatMessage['id'], ChatBite[]>();
    for (const m of messages) if (m.role === 'assistant') map.set(m.id, bitesOf(m.text, m.lists));
    return map;
  }, [messages]);
  const visibleCount = (m: ChatMessage): number => {
    const total = bitesById.get(m.id)?.length ?? 0;
    if (reducedMotion || mountIds.has(m.id)) return total;
    return Math.min(total, progress[String(m.id)] ?? 1);
  };
  const pending = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      const bites = bitesById.get(m.id) ?? [];
      const shown = visibleCount(m);
      return shown < bites.length ? { id: m.id, shown, next: bites[shown] } : null;
    }
    return null;
  })();

  const revealAll = useCallback(() => {
    setProgress((p) => {
      const next = { ...p };
      let changed = false;
      for (const m of messages) {
        if (m.role !== 'assistant' || mountIds.has(m.id)) continue;
        const total = bitesById.get(m.id)?.length ?? 0;
        if ((next[String(m.id)] ?? 1) < total) { next[String(m.id)] = total; changed = true; }
      }
      return changed ? next : p;
    });
  }, [messages, bitesById, mountIds]);

  const pendingKey = pending ? `${pending.id}:${pending.shown}` : null;
  useEffect(() => {
    if (!pending) return undefined;
    const { id, shown, next } = pending;
    const timer = setTimeout(() => {
      setProgress((p) => ({ ...p, [String(id)]: Math.max(p[String(id)] ?? 1, shown + 1) }));
    }, biteDelay(next));
    return () => clearTimeout(timer);
    // pendingKey captures id and shown; next follows from them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey]);

  // Autoscroll: follow new content only while the reader is at the bottom.
  const threadRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottom.current = true;
    setShowJump(false);
  }, []);
  const shownTotal = messages.reduce((n, m) => n + (m.role === 'assistant' ? visibleCount(m) : 1), 0);
  const contentKey = `${shownTotal}|${running ? 1 : 0}|${pending ? 1 : 0}`;
  const lastContentKey = useRef(contentKey);
  useLayoutEffect(() => {
    if (lastContentKey.current === contentKey) {
      scrollToBottom();
      return;
    }
    lastContentKey.current = contentKey;
    if (atBottom.current) scrollToBottom();
    else setShowJump(true);
    // Runs on mount (scroll to the newest) and on each content change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);
  const onScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= AT_BOTTOM_PX;
    if (atBottom.current) setShowJump(false);
  };

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const canSend = !disabled && !running && draft.trim().length > 0;
  const submit = () => {
    if (!canSend) return;
    const text = draft;
    recall.current = NOT_RECALLING;
    setDraft('');
    revealAll();
    send(text);
    scrollToBottom();
    inputRef.current?.focus();
  };

  const replaceInput = (value: string) => {
    setDraft(value);
    // Caret at the end once React has written the value.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el && el.value === value) el.setSelectionRange(value.length, value.length);
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    if (e.key === 'Enter' && !e.shiftKey) {
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === 'Escape') {
      if (running) {
        e.preventDefault();
        stop();
      } else if (recall.current.index !== null) {
        e.preventDefault();
        const { draft: saved } = recall.current;
        recall.current = NOT_RECALLING;
        replaceInput(saved);
      }
      return;
    }
    if (e.key === 'ArrowUp' && !e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      const value = el.value;
      if (value !== '' && !caretOnFirstLine(value, el.selectionStart, el.selectionEnd)) return;
      const result = recallUp(prompts, recall.current, value);
      if (!result) return;
      e.preventDefault();
      recall.current = result.state;
      replaceInput(result.value);
      return;
    }
    if (e.key === 'ArrowDown' && !e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      if (recall.current.index === null) return;
      if (!caretOnLastLine(el.value, el.selectionStart, el.selectionEnd)) return;
      const result = recallDown(prompts, recall.current);
      if (!result) return;
      e.preventDefault();
      recall.current = result.state;
      replaceInput(result.value);
    }
  };

  return (
    <div className={styles.panel} data-testid="assistant-panel" onKeyDownCapture={revealAll}>
      {notice && <p className={styles.notice} data-testid={notice.testId}>{notice.text}</p>}
      <div className={styles.threadWrap}>
      <div
        className={styles.thread}
        ref={threadRef}
        onScroll={onScroll}
        onClick={revealAll}
        aria-live="polite"
        data-testid="assistant-messages"
      >
        {messages.length === 0 && !disabled && (
          <div className={styles.examples}>
            <p>{emptyHint}</p>
            {examplePrompts.map((p) => (
              <button
                key={p}
                type="button"
                className={styles.example}
                onClick={() => { setDraft(p); inputRef.current?.focus(); }}
                data-testid="assistant-example"
              >
                {p}
              </button>
            ))}
          </div>
        )}
        {messages.map((m) => {
          if (m.role === 'user') {
            return (
              <div key={m.id} className={`${styles.bubble} ${styles.user}`} data-testid="assistant-message-user">{m.text}</div>
            );
          }
          if (m.role === 'notice' || m.role === 'error') {
            return (
              <div key={m.id} className={`${styles.line} ${styles[m.role]}`} data-testid={`assistant-message-${m.role}`}>
                <span>{m.text}</span>
                {m.retry && retry && (
                  <button type="button" className={styles.action} onClick={() => retry(m.id)} data-testid="assistant-retry">
                    Retry
                  </button>
                )}
                {m.retry && retry && retryExtra}
              </div>
            );
          }
          const bites = bitesById.get(m.id) ?? [];
          const actions = renderActions?.(m);
          return (
            <div key={m.id} className={styles.group} data-testid="assistant-message-assistant">
              {bites.slice(0, visibleCount(m)).map((b, i) => <BiteView key={i} bite={b} />)}
              {m.looked && <div className={styles.looked} data-testid="assistant-looked">{m.looked}</div>}
              {actions && <div className={styles.actions}>{actions}</div>}
            </div>
          );
        })}
        {pending && <Dots label="Typing" animated={!reducedMotion} testId="assistant-typing" />}
        {running && <Dots label="Thinking" animated={!reducedMotion} testId="assistant-thinking" />}
        {running && thinkingText && (
          <div className={styles.line} data-testid="assistant-thinking-text">{thinkingText}</div>
        )}
      </div>
      {showJump && (
        <button type="button" className={styles.jump} onClick={scrollToBottom} data-testid="assistant-jump">
          New messages ↓
        </button>
      )}
      </div>
      <div className={styles.inputRow}>
        <textarea
          ref={inputRef}
          className={styles.input}
          value={draft}
          rows={3}
          placeholder={disabled ? '' : placeholder}
          disabled={disabled}
          aria-label={inputLabel}
          data-testid="assistant-input"
          onChange={(e) => {
            // Typing in a recalled prompt makes it the draft.
            recall.current = NOT_RECALLING;
            setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
        />
        {running ? (
          <button type="button" className={styles.stop} onClick={stop} data-testid="assistant-stop">Stop</button>
        ) : (
          <button type="button" className={styles.send} disabled={!canSend} onClick={submit} data-testid="assistant-send">
            Send
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;
